import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router";
import { useMutation, useQuery } from "@apollo/client/react";
import { useTextDomainContext } from "@/app/providers";
import { BackButton, CountryDropdown } from "@/components";
import { usePageTitle } from "@/hooks";
import { mutations, queries } from "@/graphql";
import { enqueueSnackbar } from "notistack";

const inputRootSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FBFBFC",
    borderRadius: "6px",
    "& fieldset": {
      borderColor: "#E4E4E7",
    },
    "&:hover fieldset": {
      borderColor: "#CBD5E1",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#0F172A",
    },
    "& input, & textarea": {
      py: 1.25,
      px: 1.75,
      fontSize: "0.925rem",
      color: "#18181B",
      "&::placeholder": {
        color: "#94A3B8",
        opacity: 1,
      },
    },
  },
  "& .MuiSelect-select": {
    py: 1.25,
    px: 1.75,
    fontSize: "0.925rem",
    color: "#18181B",
  },
};

interface FormFieldProps {
  label: string;
  name: string;
  value: string;
  placeholder?: string;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  required?: boolean;
}

const FormField: React.FC<FormFieldProps> = ({
  label,
  name,
  value,
  placeholder,
  onChange,
  required,
}) => (
  <Box sx={{ width: "100%" }}>
    <Typography
      sx={{
        fontSize: "0.875rem",
        fontWeight: 600,
        color: "#18181B",
        mb: 0.75,
      }}
    >
      {label}
      {required && (
        <Box component="span" sx={{ color: "error.main", ml: 0.5 }}>
          *
        </Box>
      )}
    </Typography>
    <Box
      component="input"
      type="text"
      name={name}
      value={value}
      placeholder={placeholder}
      onChange={onChange}
      required={required}
      style={{
        width: "100%",
        padding: "10px 14px",
        fontSize: "0.925rem",
        color: "#18181B",
        backgroundColor: "#FBFBFC",
        border: "1px solid #E4E4E7",
        borderRadius: "6px",
        outline: "none",
        boxSizing: "border-box",
        fontFamily: "inherit",
      }}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = "#0F172A";
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = "#E4E4E7";
      }}
    />
  </Box>
);

export const AddCompany: React.FC = () => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { id: paramId, customerId: paramCustomerId } = useParams();

  // If the path ends with /add, we are strictly adding, NOT editing.
  // paramCustomerId on child-companies/add represents the parent company context, not the company being edited.
  const isAddRoute = location.pathname.endsWith("/add");
  const isEdit = useMemo(() => {
    if (isAddRoute) return false;
    return (
      location.pathname.includes("/edit") ||
      Boolean(paramId) ||
      Boolean(location.state?.isEdit) ||
      Boolean(location.state?.company && !location.pathname.endsWith("/add"))
    );
  }, [isAddRoute, location.pathname, paramId, location.state]);

  const companyId = isEdit ? paramId || "" : "";

  usePageTitle(isEdit ? gettext("Edit Company") : gettext("Add Company"));

  const backLink = useMemo(() => {
    if (location.state?.parentPath) {
      return location.state.parentPath;
    }
    const backPath = location.pathname
      .replace(/\/add$/, "")
      .replace(/\/edit\/[^/]+$/, "")
      .replace(/\/[^/]+\/edit$/, "");
    return backPath;
  }, [location.pathname, location.state]);

  const initialCustomerType = useMemo(() => {
    if (location.state?.customerType) {
      return location.state.customerType;
    }
    if (
      location.pathname.includes("child-companies") ||
      Boolean(location.state?.parentCompany) ||
      Boolean(location.state?.parentCustomerId)
    ) {
      return "child";
    }
    return "parent";
  }, [location.pathname, location.state]);

  const [formData, setFormData] = useState({
    name: "",
    cvr: "",
    pnumber: "",
    customerType: initialCustomerType,
    isFabekeOwned: false,
    parent_cvr: location.state?.parentCvr || "",
    parent_name: location.state?.parentName || "",
    customer_status: "potential",
    address: "",
    houseNumber: "",
    zip: "",
    city: "",
    country: "",
    employees: "",
    description: "",
    industryCodeNace: "",
    industryCodeSic: "",
    industryCodeIsic: "",
    industryCodeNaics: "",
    industryCodeIcb: "",
  });

  const [existingKnownNames, setExistingKnownNames] = useState<string[]>([]);

  // Query parent companies list for the parent_cvr dropdown
  const { data: parentCompaniesData } = useQuery(queries.customers, {
    variables: { customerType: "parent" },
  });

  const parentCompanies = useMemo(() => {
    const raw = (parentCompaniesData as any)?.getCustomers || [];
    return raw.filter(
      (c: any) =>
        (c.customerType === "parent" || c.customerType !== "child") &&
        (!companyId || c.id !== companyId),
    );
  }, [parentCompaniesData, companyId]);

  // Pre-select parent company if coming from a parent company page/action
  useEffect(() => {
    if (formData.customerType === "child" && parentCompanies.length > 0) {
      const targetParentId =
        location.state?.parentCustomerId ||
        (!isEdit ? paramCustomerId : undefined);
      const targetParentCvr =
        location.state?.parentCvr || location.state?.parentCompany?.cvr;

      if (!formData.parent_cvr) {
        let matched: any = null;
        if (targetParentCvr) {
          matched = parentCompanies.find((p: any) => p.cvr === targetParentCvr);
        } else if (targetParentId) {
          matched = parentCompanies.find((p: any) => p.id === targetParentId);
        }

        if (matched) {
          setFormData((prev) => ({
            ...prev,
            parent_cvr: matched.cvr || "",
            parent_name: matched.name || "",
          }));
        }
      } else if (!formData.parent_name) {
        const matched = parentCompanies.find(
          (p: any) => p.cvr === formData.parent_cvr,
        );
        if (matched?.name) {
          setFormData((prev) => ({
            ...prev,
            parent_name: matched.name,
          }));
        }
      }
    }
  }, [
    formData.customerType,
    formData.parent_cvr,
    formData.parent_name,
    parentCompanies,
    location.state,
    paramCustomerId,
    isEdit,
  ]);

  // Query customer details only in edit mode
  const { data: customerData } = useQuery(queries.customers, {
    variables: { customerId: companyId },
    skip: !isEdit || !companyId,
  });

  useEffect(() => {
    if (!isEdit) return;

    const existing =
      (customerData as any)?.getCustomers?.[0] || location.state?.company;
    if (existing) {
      setFormData((prev) => ({
        ...prev,
        name: existing.name || "",
        cvr: existing.cvr || existing.ident || "",
        pnumber: existing.pnumber || "",
        customerType: existing.customerType || "parent",
        isFabekeOwned: Boolean(existing.isFabekeOwned),
        parent_cvr: existing.parentCvr || existing.parent_cvr || "",
        parent_name: existing.parentName || existing.parent_name || "",
        customer_status:
          existing.customerStatus || existing.customer_status || "potential",
        address: existing.address || "",
        houseNumber: existing.houseNumber || "",
        zip: existing.zip || "",
        city: existing.city || "",
        country: existing.country || "",
        employees: existing.employees || existing.employess || "",
        description: existing.description || "",
        industryCodeNace: existing.industryCodeNace || "",
        industryCodeSic: existing.industryCodeSic || "",
        industryCodeIsic: existing.industryCodeIsic || "",
        industryCodeNaics: existing.industryCodeNaics || "",
        industryCodeIcb: existing.industryCodeIcb || "",
      }));

      const known = existing.knownNames || existing.known_names || [];
      if (Array.isArray(known)) {
        setExistingKnownNames(known);
      }
    }
  }, [isEdit, customerData, location.state]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleCancel = () => {
    if (backLink) {
      navigate(backLink);
    } else {
      navigate(-1 as any);
    }
  };

  const [addCompanyMutation, { loading: addingCompany }] = useMutation(
    mutations.addCompany,
  );
  const [updateCompanyMutation, { loading: updatingCompany }] = useMutation(
    mutations.updateCompany,
  );

  const isSaving = addingCompany || updatingCompany;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      enqueueSnackbar(gettext("Company name is required"), {
        variant: "warning",
      });
      return;
    }

    if (formData.customerType === "child" && !formData.parent_cvr) {
      enqueueSnackbar(
        gettext("Parent company is required for child companies"),
        {
          variant: "warning",
        },
      );
      return;
    }

    // Auto-resolve parent name from parent_cvr if not already resolved
    let resolvedParentName = formData.parent_name;
    if (formData.customerType === "child" && formData.parent_cvr) {
      const parent = parentCompanies.find(
        (p: any) => p.cvr === formData.parent_cvr,
      );
      if (parent?.name) {
        resolvedParentName = parent.name;
      }
    }

    const companyNameTrimmed = formData.name.trim();
    const updatedKnownNames = [
      companyNameTrimmed,
      ...existingKnownNames.filter((n) => n !== companyNameTrimmed),
    ];

    const companyPayload: Record<string, any> = {
      name: companyNameTrimmed,
      ident: formData.cvr.trim(),
      cvr: formData.cvr.trim(),
      country: formData.country || "",
      type: "company",
      customer_type: formData.customerType || "parent",
      isFabekeOwned: Boolean(formData.isFabekeOwned),
      parent_cvr:
        formData.customerType === "child" ? formData.parent_cvr : null,
      parent_name:
        formData.customerType === "child" ? resolvedParentName || null : null,
      customer_status: formData.customer_status || "potential",
      known_names: updatedKnownNames,
      pnumber: formData.pnumber,
      address: formData.address,
      houseNumber: formData.houseNumber,
      zip: formData.zip,
      city: formData.city,
      employees: formData.employees,
      description: formData.description,
      industryCodeNace: formData.industryCodeNace,
      industryCodeSic: formData.industryCodeSic,
      industryCodeIsic: formData.industryCodeIsic,
      industryCodeNaics: formData.industryCodeNaics,
      industryCodeIcb: formData.industryCodeIcb,
    };

    try {
      if (isEdit && companyId) {
        const res: any = await updateCompanyMutation({
          variables: {
            customerId: companyId,
            customerData: companyPayload,
          },
          refetchQueries: [
            { query: queries.customers, variables: { customerType: "parent" } },
            { query: queries.customers, variables: { customerType: "child" } },
            { query: queries.customers, variables: { customerId: companyId } },
          ],
        });

        if (res?.data?.updateCompany?.success) {
          enqueueSnackbar(gettext("Company updated successfully"), {
            variant: "success",
          });
          handleCancel();
        } else {
          enqueueSnackbar(
            res?.data?.updateCompany?.message?.[0] ||
              gettext("Failed to update company"),
            { variant: "error" },
          );
        }
      } else {
        const res: any = await addCompanyMutation({
          variables: {
            companyData: companyPayload,
          },
          refetchQueries: [
            { query: queries.customers, variables: { customerType: "parent" } },
            { query: queries.customers, variables: { customerType: "child" } },
          ],
        });

        if (res?.data?.addCompany?.success) {
          enqueueSnackbar(gettext("Company added successfully"), {
            variant: "success",
          });
          handleCancel();
        } else {
          enqueueSnackbar(
            res?.data?.addCompany?.message?.[0] ||
              gettext("Failed to add company"),
            { variant: "error" },
          );
        }
      }
    } catch (err: any) {
      enqueueSnackbar(
        err?.message || gettext("An error occurred while saving"),
        {
          variant: "error",
        },
      );
    }
  };

  return (
    <Box sx={{ width: "100%", py: 1 }}>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        {/* Header Row: Title & Subtitle on left, Action Buttons on right */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "flex-start" },
            gap: 2,
            mb: 4,
          }}
        >
          <Box sx={{ maxWidth: { xs: "100%", sm: "60%" } }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "#18181B",
                fontSize: { xs: "1.4rem", sm: "1.65rem" },
                letterSpacing: "-0.01em",
              }}
            >
              {isEdit ? gettext("Edit Company") : gettext("Add Company")}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#71717A",
                mt: 0.75,
                lineHeight: 1.5,
                fontSize: "0.9rem",
              }}
            >
              {isEdit
                ? gettext(
                    "Update company profile information, address details, and industry classification codes.",
                  )
                : gettext(
                    "Fill in the details and industry specifications to create a new company profile.",
                  )}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              alignSelf: { xs: "stretch", sm: "auto" },
              justifyContent: { xs: "flex-end", sm: "auto" },
            }}
          >
            {backLink && <BackButton link={backLink} />}
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={isSaving}
              sx={{
                color: "#18181B",
                borderColor: "#E4E4E7",
                backgroundColor: "#FFFFFF",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.9rem",
                borderRadius: "6px",
                px: 3,
                py: 0.85,
                "&:hover": {
                  borderColor: "#CBD5E1",
                  backgroundColor: "#F4F4F5",
                },
              }}
            >
              {gettext("Cancel")}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSaving}
              sx={{
                backgroundColor: "#0F172A",
                color: "#FFFFFF",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.9rem",
                borderRadius: "6px",
                px: 3,
                py: 0.85,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#1E293B",
                  boxShadow: "none",
                },
              }}
            >
              {isSaving ? (
                <CircularProgress size={20} sx={{ color: "#FFFFFF" }} />
              ) : (
                gettext("Save Changes")
              )}
            </Button>
          </Box>
        </Box>

        {/* Section 1: Details */}
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "1.1rem",
            color: "#18181B",
            mb: 2.5,
          }}
        >
          {gettext("Details")}
        </Typography>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Row 1 */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormField
              label={gettext("Company Name")}
              name="name"
              value={formData.name}
              placeholder={gettext("Enter company name")}
              onChange={handleChange}
              required
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormField
              label={gettext("Company Number")}
              name="cvr"
              value={formData.cvr}
              placeholder={gettext("Enter company number")}
              onChange={handleChange}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormField
              label={gettext("Production Number")}
              name="pnumber"
              value={formData.pnumber}
              placeholder={gettext("Enter production number")}
              onChange={handleChange}
            />
          </Grid>

          {/* Row 2 */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Box sx={{ width: "100%" }}>
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: "#18181B",
                  mb: 0.75,
                }}
              >
                {gettext("Company Type")}
              </Typography>
              <FormControl fullWidth size="small" sx={inputRootSx}>
                <Select
                  name="customerType"
                  value={formData.customerType}
                  displayEmpty
                  onChange={(e) => {
                    const newType = e.target.value as string;
                    setFormData((prev) => ({
                      ...prev,
                      customerType: newType,
                      parent_cvr: newType === "parent" ? "" : prev.parent_cvr,
                      parent_name: newType === "parent" ? "" : prev.parent_name,
                    }));
                  }}
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <Typography
                          sx={{ color: "#94A3B8", fontSize: "0.925rem" }}
                        >
                          {gettext("Select company type")}
                        </Typography>
                      );
                    }
                    return selected === "parent"
                      ? gettext("Parent")
                      : selected === "child"
                        ? gettext("Child")
                        : selected;
                  }}
                >
                  <MenuItem value="parent">{gettext("Parent")}</MenuItem>
                  <MenuItem value="child">{gettext("Child")}</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Grid>

          {formData.customerType === "child" && (
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Box sx={{ width: "100%" }}>
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    color: "#18181B",
                    mb: 0.75,
                  }}
                >
                  {gettext("Parent Company")}
                  <Box component="span" sx={{ color: "error.main", ml: 0.5 }}>
                    *
                  </Box>
                </Typography>
                <Autocomplete
                  id="parent-company-autocomplete"
                  options={parentCompanies}
                  getOptionLabel={(option: any) => {
                    if (!option) return "";
                    if (typeof option === "string") return option;
                    return option.name
                      ? `${option.name} (${option.cvr || option.ident || ""})`
                      : option.cvr || option.ident || "";
                  }}
                  isOptionEqualToValue={(option: any, value: any) => {
                    const optVal =
                      typeof option === "string"
                        ? option
                        : option?.cvr || option?.ident;
                    const valVal =
                      typeof value === "string"
                        ? value
                        : value?.cvr || value?.ident;
                    return optVal === valVal;
                  }}
                  value={
                    parentCompanies.find(
                      (p: any) =>
                        (p.cvr && p.cvr === formData.parent_cvr) ||
                        (p.ident && p.ident === formData.parent_cvr),
                    ) || null
                  }
                  onChange={(_event, newValue: any) => {
                    setFormData((prev) => ({
                      ...prev,
                      parent_cvr: newValue?.cvr || newValue?.ident || "",
                      parent_name: newValue?.name || "",
                    }));
                  }}
                  size="small"
                  fullWidth
                  sx={inputRootSx}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={gettext("Search parent company...")}
                      required={!formData.parent_cvr}
                    />
                  )}
                />
              </Box>
            </Grid>
          )}

          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Box sx={{ width: "100%" }}>
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: "#18181B",
                  mb: 0.75,
                }}
              >
                {gettext("Customer Status")}
              </Typography>
              <FormControl fullWidth size="small" sx={inputRootSx}>
                <Select
                  name="customer_status"
                  value={formData.customer_status}
                  displayEmpty
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      customer_status: e.target.value as string,
                    }))
                  }
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <Typography
                          sx={{ color: "#94A3B8", fontSize: "0.925rem" }}
                        >
                          {gettext("Select customer status")}
                        </Typography>
                      );
                    }
                    return selected === "potential"
                      ? gettext("Potential")
                      : selected === "testing"
                        ? gettext("Testing")
                        : selected;
                  }}
                >
                  <MenuItem value="potential">{gettext("Potential")}</MenuItem>
                  <MenuItem value="testing">{gettext("Testing")}</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: "#18181B",
                  mb: 0.75,
                }}
              >
                {gettext("Fabeke Owned")}
              </Typography>
              <Box
                sx={{
                  height: "44px",
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#FBFBFC",
                  border: "1px solid #E4E4E7",
                  borderRadius: "6px",
                  px: 2,
                  boxSizing: "border-box",
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isFabekeOwned}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          isFabekeOwned: e.target.checked,
                        }))
                      }
                      color="primary"
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontSize: "0.9rem",
                        color: "#18181B",
                        fontWeight: 500,
                      }}
                    >
                      {formData.isFabekeOwned ? gettext("Yes") : gettext("No")}
                    </Typography>
                  }
                  sx={{ m: 0 }}
                />
              </Box>
            </Box>
          </Grid>

          {/* Row 3 */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormField
              label={gettext("Address")}
              name="address"
              value={formData.address}
              placeholder={gettext("Enter street address")}
              onChange={handleChange}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormField
              label={gettext("House Number")}
              name="houseNumber"
              value={formData.houseNumber}
              placeholder={gettext("Enter house number")}
              onChange={handleChange}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormField
              label={gettext("Zip Code")}
              name="zip"
              value={formData.zip}
              placeholder={gettext("Enter zip code")}
              onChange={handleChange}
            />
          </Grid>

          {/* Row 4 */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormField
              label={gettext("City")}
              name="city"
              value={formData.city}
              placeholder={gettext("Enter city")}
              onChange={handleChange}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Box sx={{ width: "100%" }}>
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: "#18181B",
                  mb: 0.75,
                }}
              >
                {gettext("Country")}
              </Typography>
              <CountryDropdown
                defaultValue={formData.country}
                componentLabel=""
                onChange={(event: any) => {
                  const val =
                    typeof event === "object"
                      ? event?.value || event?.target?.value
                      : event;
                  setFormData((prev) => ({
                    ...prev,
                    country: val || "",
                  }));
                }}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormField
              label={gettext("Employees")}
              name="employees"
              value={formData.employees}
              placeholder={gettext("Enter number of employees")}
              onChange={handleChange}
            />
          </Grid>

          {/* Row 5 */}
          <Grid size={{ xs: 12 }}>
            <FormField
              label={gettext("Description")}
              name="description"
              value={formData.description}
              placeholder={gettext("Enter company description")}
              onChange={handleChange}
            />
          </Grid>
        </Grid>

        {/* Section 2: Specifications */}
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "1.1rem",
            color: "#18181B",
            mb: 2.5,
            mt: 4,
          }}
        >
          {gettext("Specifications")}
        </Typography>

        <Grid container spacing={3}>
          {/* Row 1 */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormField
              label={gettext("Industry Code: Nace")}
              name="industryCodeNace"
              value={formData.industryCodeNace}
              placeholder={gettext("Enter NACE code")}
              onChange={handleChange}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormField
              label={gettext("Industry Code: SIC")}
              name="industryCodeSic"
              value={formData.industryCodeSic}
              placeholder={gettext("Enter SIC code")}
              onChange={handleChange}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormField
              label={gettext("Industry Code: ISIC")}
              name="industryCodeIsic"
              value={formData.industryCodeIsic}
              placeholder={gettext("Enter ISIC code")}
              onChange={handleChange}
            />
          </Grid>

          {/* Row 2 */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormField
              label={gettext("Industry Code: NAICS")}
              name="industryCodeNaics"
              value={formData.industryCodeNaics}
              placeholder={gettext("Enter NAICS code")}
              onChange={handleChange}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormField
              label={gettext("Industry Code: ICB")}
              name="industryCodeIcb"
              value={formData.industryCodeIcb}
              placeholder={gettext("Enter ICB code")}
              onChange={handleChange}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};
